import React, { useState, useEffect, useCallback, useRef } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { Loader2 } from 'lucide-react';
import {
  supabase,
  fetchUserCredits,
  getAdminRole,
  consumeSsoTokensFromHash,
  createProject as createProjectRow,
  brandingForNewProject,
  fetchBrandKits,
  importAssetFromImageMotion,
  importDuskImageFromHyperDusk,
  saveEditorDocument,
  type SavedBrandKit,
} from './services/supabase';
import type { AdminRole } from '@rehub/admin';
import type { AppView, MotionSource, StartWorkflow } from './types';
import Login from './components/Login';
import LandingPage from './components/LandingPage';
import Dashboard from './components/Dashboard';
import FormatPicker, { type NewProjectFormat } from './components/FormatPicker';
import VideoEditor from './components/editor/VideoEditor';
import { draftTitle } from './components/editor/model';
import Header from './components/Header';
import AdminPanel from './components/AdminPanel';
import Footer from './components/Footer';
import FluidBackground from './components/FluidBackground';
import { appendClip } from '@rehub/propertycut-timeline';

// ---------------------------------------------------------------------
// Inbound handoff helpers
// ---------------------------------------------------------------------

/**
 * Cross-app deep-link payload set by ImageMotion / Hyper Dusk's
 * "Send to PropertyCuts" button:
 *
 *   /?source=imagemotion&projectId=<id>
 *   /?source=hyperdusk&projectId=<id>
 *   /?source=hyperdusk&projectId=<id>&imageId=<id>
 */
interface IncomingHandoff {
  source: MotionSource;
  imagemotionProjectId: string;
  imagemotionImageId: string | null;
}

// ---------------------------------------------------------------------
// Deep-link helpers — each project gets its own URL (/p/<id>) so it can be
// bookmarked, shared internally, and opened in a new tab. Back/forward
// navigate between the dashboard and an open project. This matches the
// /p/{id} convention used by Video Motion, ImageMotion and Hyper Dusk.
// ---------------------------------------------------------------------

function readProjectIdFromUrl(): string | null {
  if (typeof window === 'undefined') return null;
  const match = window.location.pathname.match(/^\/p\/([0-9a-fA-F-]+)$/);
  return match ? match[1] : null;
}

/**
 * Reflect the open project in the address bar as /p/<id>. `replace` swaps the
 * current history entry (used on first load / handoff) instead of pushing a
 * new one, so we don't pollute the back stack.
 */
function syncProjectUrl(projectId: string | null, replace = false): void {
  if (typeof window === 'undefined') return;
  const url = projectId ? `/p/${projectId}` : '/';
  const current = window.location.pathname + window.location.search;
  if (current === url) return;
  if (replace) window.history.replaceState({}, document.title, url);
  else window.history.pushState({}, document.title, url);
}

function consumeIncomingHandoff(): IncomingHandoff | null {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  const sourceRaw = params.get('source');
  const projectId = params.get('projectId');
  if (!sourceRaw || !projectId) return null;
  if (sourceRaw !== 'imagemotion' && sourceRaw !== 'hyperdusk') return null;
  const imageId = params.get('imageId');
  // Strip the params off the URL so a refresh doesn't re-trigger.
  params.delete('source');
  params.delete('projectId');
  params.delete('imageId');
  const remaining = params.toString();
  const newUrl = window.location.pathname + (remaining ? `?${remaining}` : '');
  window.history.replaceState({}, document.title, newUrl);
  return {
    source: sourceRaw as MotionSource,
    imagemotionProjectId: projectId,
    imagemotionImageId: imageId,
  };
}

export default function App() {
  const [view, setView] = useState<AppView>('LOADING');
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [userCredits, setUserCredits] = useState(0);
  const [adminRole, setAdminRole] = useState<AdminRole | null>(null);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [brandKits, setBrandKits] = useState<SavedBrandKit[] | null>(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [authMode, setAuthMode] = useState<'login' | 'signup'>('login');
  const [importing, setImporting] = useState<{ source: MotionSource; message: string } | null>(null);
  const intentionalSignOutRef = useRef(false);
  // Captured before auth resolves; processed once the user is ready.
  const pendingHandoffRef = useRef<IncomingHandoff | null>(null);

  // Open a project into the editor and reflect it in the URL.
  const openProject = useCallback((id: string) => {
    setSelectedProjectId(id);
    setView('EDITOR');
    syncProjectUrl(id);
  }, []);

  // Return to the dashboard and clear the project from the URL.
  const goToDashboard = useCallback(() => {
    setSelectedProjectId(null);
    setView('DASHBOARD');
    syncProjectUrl(null);
  }, []);

  const loadCredits = useCallback(async (userId: string) => {
    try {
      const [credits, role] = await Promise.all([
        fetchUserCredits(userId),
        getAdminRole(userId),
      ]);
      setUserCredits(credits);
      setAdminRole(role);
    } catch (err) {
      console.warn('Failed to fetch credits:', err);
    }
  }, []);

  /**
   * Auto-create a draft project and import the asset coming from
   * imagemotion / hyperdusk. Lands the user straight in the Editor.
   */
  const processIncomingHandoff = useCallback(
    async (userId: string, handoff: IncomingHandoff) => {
      const sourceLabel = handoff.source === 'hyperdusk' ? 'Hyper Dusk' : 'ImageMotion';
      setImporting({ source: handoff.source, message: `Importing from ${sourceLabel}…` });
      try {
        const branding = await brandingForNewProject(userId);
        const projectId = await createProjectRow({
          userId,
          templateId: 'classic-listing',
          title: draftTitle('landscape'),
          outputFormat: 'landscape',
          editorState: branding.editorState,
          agentData: branding.agent,
        });

        const asset = handoff.source === 'hyperdusk' && handoff.imagemotionImageId
          ? await importDuskImageFromHyperDusk({
            userId,
            projectId,
            imagemotionProjectId: handoff.imagemotionProjectId,
            imagemotionImageId: handoff.imagemotionImageId,
          })
          : await importAssetFromImageMotion({
            userId,
            projectId,
            imagemotionProjectId: handoff.imagemotionProjectId,
          });

        await saveEditorDocument({
          projectId,
          editorState: appendClip(branding.editorState, {
            id: asset.id,
            kind: asset.kind,
            durationSeconds: asset.duration_seconds,
          }),
          outputFormat: 'landscape',
          title: draftTitle('landscape'),
          listing: {},
          agent: branding.agent,
          status: 'draft',
        });

        setSelectedProjectId(projectId);
        setView('EDITOR');
        syncProjectUrl(projectId, true);
      } catch (err: any) {
        console.warn('[propertycut] handoff import failed:', err);
        alert(`Couldn't import from ${sourceLabel}: ${err?.message ?? err}`);
        setView('DASHBOARD');
      } finally {
        setImporting(null);
      }
    },
    [],
  );

  useEffect(() => {
    let cancelled = false;

    // Capture deep-link handoff params *before* SSO/session work runs —
    // SSO would otherwise also strip them as part of `consumeSsoTokensFromHash`.
    pendingHandoffRef.current = consumeIncomingHandoff();

    const finishAuth = (signedInUser: User) => {
      // If a handoff was attached to the inbound URL, process it instead
      // of landing on the dashboard. Errors fall back to dashboard.
      const handoff = pendingHandoffRef.current;
      pendingHandoffRef.current = null;
      if (handoff) {
        // Importing splash takes over the screen until done.
        setView('DASHBOARD');
        void processIncomingHandoff(signedInUser.id, handoff);
        return;
      }
      // Honour a project deep link (/p/<id>) so refreshing or opening
      // a shared/bookmarked URL lands straight in that project's editor.
      const deepLinkProjectId = readProjectIdFromUrl();
      if (deepLinkProjectId) {
        setSelectedProjectId(deepLinkProjectId);
        setView('EDITOR');
        return;
      }
      setView('DASHBOARD');
    };

    const initAuth = async () => {
      // 1. SSO tokens from cross-app navigation (matches videomotion App.tsx:65–89)
      const ssoTokens = consumeSsoTokensFromHash();
      if (ssoTokens) {
        try {
          const { data, error } = await supabase.auth.setSession({
            access_token: ssoTokens.accessToken,
            refresh_token: ssoTokens.refreshToken,
          });
          if (data.session && !cancelled) {
            setSession(data.session);
            setUser(data.session.user);
            loadCredits(data.session.user.id);
            finishAuth(data.session.user);
            return;
          }
          if (error) console.warn('SSO session failed:', error.message);
        } catch (err) {
          console.warn('SSO session error:', err);
        }
      }

      // 2. Magic-link callback path
      const isAuthCallback = window.location.pathname === '/auth/callback';
      if (isAuthCallback) {
        setTimeout(() => {
          if (!cancelled) {
            window.history.replaceState({}, document.title, '/');
            supabase.auth.getSession().then(({ data: { session: s } }) => {
              if (s && !cancelled) {
                setSession(s);
                setUser(s.user);
                loadCredits(s.user.id);
                finishAuth(s.user);
              } else if (!cancelled) {
                setView('LOGIN');
              }
            });
          }
        }, 1500);
        return;
      }

      // 3. Existing session
      try {
        const { data: { session: existingSession } } = await supabase.auth.getSession();
        if (cancelled) return;

        if (!existingSession) {
          setView('LOGIN');
          return;
        }

        const { data: { user: validatedUser }, error: userError } = await supabase.auth.getUser();
        if (cancelled) return;

        if (validatedUser && !userError) {
          setSession(existingSession);
          setUser(validatedUser);
          loadCredits(validatedUser.id);
          finishAuth(validatedUser);
        } else {
          setView('LOGIN');
        }
      } catch (err) {
        console.warn('Auth init error:', err);
        if (!cancelled) setView('LOGIN');
      }
    };

    initAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, newSession) => {
      if (cancelled) return;
      if (event === 'SIGNED_OUT') {
        setSession(null);
        setUser(null);
        setUserCredits(0);
        setAdminRole(null);
        setShowAdminPanel(false);
        if (!intentionalSignOutRef.current) {
          setView('LOGIN');
        }
        intentionalSignOutRef.current = false;
        return;
      }
      if (newSession) {
        setSession(newSession);
        setUser(newSession.user);
        loadCredits(newSession.user.id);
        if (view === 'LOGIN' || view === 'LOADING') {
          // If a handoff was queued (e.g. user logged in *after* arriving
          // with ?source=…), process it here. Otherwise just land on the
          // dashboard.
          const handoff = pendingHandoffRef.current;
          pendingHandoffRef.current = null;
          if (handoff) {
            setView('DASHBOARD');
            void processIncomingHandoff(newSession.user.id, handoff);
          } else {
            const deepLinkProjectId = readProjectIdFromUrl();
            if (deepLinkProjectId) {
              setSelectedProjectId(deepLinkProjectId);
              setView('EDITOR');
            } else {
              setView('DASHBOARD');
            }
          }
        }
      }
    });

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadCredits]);

  const handleSignOut = useCallback(async () => {
    intentionalSignOutRef.current = true;
    syncProjectUrl(null, true);
    await supabase.auth.signOut();
    setView('LOGIN');
  }, []);

  // Keep the view in sync with the address bar when the user presses the
  // browser back/forward buttons.
  useEffect(() => {
    const onPopState = () => {
      if (!session || !user) return;
      const id = readProjectIdFromUrl();
      if (id) {
        setSelectedProjectId(id);
        setView('EDITOR');
      } else {
        setSelectedProjectId(null);
        setView((cur) => (cur === 'EDITOR' ? 'DASHBOARD' : cur));
      }
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [session, user]);

  useEffect(() => {
    if (!user || view !== 'FORMAT_PICKER') return;
    let cancelled = false;
    fetchBrandKits(user.id)
      .then((kits) => {
        if (!cancelled) setBrandKits(kits);
      })
      .catch((err) => {
        console.warn('[propertycut] saved branding load failed:', err);
      });
    return () => {
      cancelled = true;
    };
  }, [user, view]);

  const handleSelectFormat = useCallback(async (
    format: NewProjectFormat,
    workflow: StartWorkflow,
    brandKitId?: string | null,
  ) => {
    if (!user) return;
    setCreating(true);
    try {
      const branding = await brandingForNewProject(
        user.id,
        brandKitId === undefined ? undefined : { kitId: brandKitId },
      );
      const projectId = await createProjectRow({
        userId: user.id,
        templateId: 'classic-listing',
        title: draftTitle(format),
        outputFormat: format,
        editorState: branding.editorState,
        agentData: branding.agent,
        startWorkflow: workflow,
      });
      openProject(projectId);
    } catch (err: any) {
      alert(`Failed to create project: ${err?.message ?? err}`);
    } finally {
      setCreating(false);
    }
  }, [user, openProject]);

  if (view === 'LOADING') {
    return (
      <div className="min-h-screen relative overflow-hidden bg-slate-950 text-slate-50">
        <FluidBackground />
        <div className="relative z-10 flex items-center justify-center min-h-screen">
          <div className="text-center animate-fade-in">
            <img
              src="/logo.png"
              alt="PropertyCuts"
              className="w-[280px] max-w-full h-auto mx-auto mb-6 drop-shadow-[0_0_24px_rgba(255,255,255,0.12)]"
            />
            <Loader2 className="w-6 h-6 mx-auto text-teal-400 animate-spin" />
          </div>
        </div>
      </div>
    );
  }

  if (view === 'LOGIN' || !session || !user) {
    return (
      <div className="min-h-screen flex flex-col bg-slate-950 relative overflow-hidden text-slate-50">
        <FluidBackground />
        <div className="relative z-10 flex flex-col flex-1 min-h-0 overflow-y-auto">
          <Header
            onLogin={() => { setAuthMode('login'); setShowLoginModal(true); }}
            onSignUp={() => { setAuthMode('signup'); setShowLoginModal(true); }}
          />
          <LandingPage
            onLogin={() => { setAuthMode('login'); setShowLoginModal(true); }}
            onSignUp={() => { setAuthMode('signup'); setShowLoginModal(true); }}
          />
          <div className="shrink-0">
            <Footer />
          </div>
          {showLoginModal && (
            <div
              className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
              onClick={() => setShowLoginModal(false)}
            >
              <div className="w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
                <Login
                  initialMode={authMode}
                  onAuthSuccess={() => { setShowLoginModal(false); setView('DASHBOARD'); }}
                  onClose={() => setShowLoginModal(false)}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (view === 'EDITOR' && selectedProjectId) {
    return (
      <VideoEditor
        userId={user.id}
        projectId={selectedProjectId}
        credits={userCredits}
        onBack={goToDashboard}
        onCredits={setUserCredits}
      />
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-slate-950 relative overflow-hidden text-slate-50">
      <FluidBackground />

      <div className="relative z-10 flex flex-col flex-1 min-h-screen">
        <Header
          onReset={goToDashboard}
          userEmail={user.email ?? null}
          userCredits={userCredits}
          onSignOut={handleSignOut}
          isAdmin={adminRole !== null}
          onOpenAdmin={() => setShowAdminPanel(true)}
        />

        <main className="flex-1 max-w-6xl w-full mx-auto px-6 py-10">
          {view === 'DASHBOARD' && (
            <Dashboard
              userId={user.id}
              onNewVideo={() => setView('FORMAT_PICKER')}
              onOpenProject={openProject}
            />
          )}
          {view === 'FORMAT_PICKER' && (
            <FormatPicker
              busy={creating}
              brandKits={brandKits}
              onSelect={handleSelectFormat}
              onCancel={() => setView('DASHBOARD')}
            />
          )}
          {view === 'RENDERS' && (
            <div className="text-center py-20 text-slate-500">
              <p className="text-sm">
                Renders coming in Sprint 2 — project {selectedProjectId}
              </p>
              <button
                onClick={() => setView('DASHBOARD')}
                className="mt-4 text-teal-400 hover:text-teal-300 text-sm"
              >
                ← Back to dashboard
              </button>
            </div>
          )}
        </main>

        <Footer />

        {showAdminPanel && adminRole && (
          <AdminPanel adminRole={adminRole} onClose={() => setShowAdminPanel(false)} />
        )}

        {importing && (
          <div className="fixed inset-0 z-[60] grid place-items-center bg-black/85 backdrop-blur-sm">
            <div className="text-center">
              <Loader2 className="w-8 h-8 mx-auto animate-spin text-teal-300" />
              <p className="mt-3 text-sm text-slate-200">{importing.message}</p>
              <p className="mt-1 text-xs text-slate-500">
                Copying the asset across — this can take a moment for video files.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
